import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { ColorPickerRGBA, stringToRgba, rgbaToString } from '@/components/ColorPickerRGBA';
import { CompactColorPicker } from '@/components/ui/CompactColorPicker';
import { UploadControl } from '@/components/UploadControl';
import { VideoFeatures, AutoplayOverlay } from '@/store/videoFeatures';
import { VideoTestData } from '@/components/ab-test/types';
import { templates } from './autoplayTemplates';
import { Button } from '@/components/ui/button';
import { Check, Copy, Info, Calendar, CheckCircle2, AlertCircle, TestTube, BarChart3, Trash2, PlayCircle, Activity, Square, RefreshCcw, SlidersHorizontal, TrendingUp, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TrafficEditModal } from '@/components/ab-test/TrafficEditModal';
import { Checkbox } from '@/components/ui/checkbox';
import { AutoplayMetricsTable } from './AutoplayMetricsTable';
import { AutoplayTestTab } from './tabs/AutoplayTestTab';
import { FinalizeAutoplayTestModal } from './FinalizeAutoplayTestModal';
import { ConnectedPlatformsModal } from './ConnectedPlatformsModal';
import { useAutoplayRealTimeMetrics } from '@/hooks/useAutoplayRealTimeMetrics';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { DateRange } from 'react-day-picker';
import { RetentionChartContainer } from '@/components/ab-test/retention/RetentionChartContainer';
import { generateRetentionData } from '@/components/ab-test/retention/RetentionUtils';

import { AutoplayPersonalizationTab } from './tabs/AutoplayPersonalizationTab';
import { AutoplayMetricsTab } from './tabs/AutoplayMetricsTab';

// Helper to ensure arrays are always defined
const safeArray = <T,>(arr: T[] | undefined | null): T[] => {
  return Array.isArray(arr) ? arr : [];
};

interface AutoplayUnifiedConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  onActiveTabChange?: (tab: string) => void;
}

export const AutoplayUnifiedConfig: React.FC<AutoplayUnifiedConfigProps> = ({ 
  features, 
  setFeature,
  containerRef,
  onActiveTabChange
}) => {
  useLanguage();
  const [activeTab, setActiveTab] = useState<string>('basic');
  const [metricsSubTab, setMetricsSubTab] = useState<'overview' | 'retention'>('overview');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)),
    to: new Date()
  });
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [trafficModalOpen, setTrafficModalOpen] = useState(false);
  const [platformsModalOpen, setPlatformsModalOpen] = useState(false);

  // Mock data for platforms
  const mockPlatforms = [
    {
      id: 'HO',
      name: 'Hotmart',
      badge: 'Ativa',
      conversions: 42,
      averageTicket: 'R$ 293,95',
      revenue: 'R$ 12.345,67',
      since: '12 de ago de 2025',
      isFavorite: true
    },
    {
      id: 'BR',
      name: 'Braip',
      badge: 'Ativa',
      conversions: 28,
      averageTicket: 'R$ 305,08',
      revenue: 'R$ 8.542,30',
      since: '01 de set de 2025',
      isFavorite: false
    }
  ];

  // Mock data for traffic distribution modal
  const mockAutoplayVideos: VideoTestData[] = [
    {
      id: 'clean',
      name: 'VSL Principal com Headline Clean',
      thumbnail: '/placeholder.svg',
      trafficPercentage: 33.3,
      views: 1141,
      viewsUnique: 822,
      plays: 742,
      playsUnique: 630,
      playRate: 65.0,
      avgEngagement: 76.0,
      pitchRetention: 58.7,
      buttonClicks: 111,
      conversions: 47,
      conversionRate: 6.3,
      revenue: 5466,
      platforms: ['HO Hotmart']
    },
    {
      id: 'with-button',
      name: 'VSL com Botão de Ação',
      thumbnail: '/placeholder.svg',
      trafficPercentage: 33.3,
      views: 1098,
      viewsUnique: 789,
      plays: 712,
      playsUnique: 601,
      playRate: 64.8,
      avgEngagement: 74.5,
      pitchRetention: 56.2,
      buttonClicks: 118,
      conversions: 51,
      conversionRate: 7.2,
      revenue: 5916,
      platforms: ['HO Hotmart']
    },
    {
      id: 'minimal',
      name: 'VSL Minimalista',
      thumbnail: '/placeholder.svg',
      trafficPercentage: 33.4,
      views: 1185,
      viewsUnique: 854,
      plays: 771,
      playsUnique: 660,
      playRate: 65.1,
      avgEngagement: 77.2,
      pitchRetention: 60.1,
      buttonClicks: 104,
      conversions: 44,
      conversionRate: 5.7,
      revenue: 5084,
      platforms: ['HO Hotmart']
    },
  ];
  
  const autoplayOverlay = features.autoplayOverlay || {
    enabled: false,
    textTop: 'Seu vídeo já começou',
    textBottom: 'Clique para ativar o som',
    showIcon: true,
    pulseAnimation: false,
    whiteBorder: false,
    autoRestart: false,
    useTemplate: true,
    templateId: 'clean',
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: '400',
    colors: {
      background: 'rgba(255, 255, 255, 0.9)',
      text: '#000000',
      icon: '#000000'
    },
    abTestConfig: {
      enabled: false,
      variations: [],
      trafficDistribution: {},
      variantTrafficDistribution: {},
      status: 'draft',
      isRealTimeEnabled: false,
      lastUpdated: ''
    }
  };

  const abTestConfig = {
    enabled: autoplayOverlay.abTestConfig?.enabled ?? false,
    variations: safeArray(autoplayOverlay.abTestConfig?.variations),
    selectedTemplates: safeArray(autoplayOverlay.abTestConfig?.selectedTemplates),
    status: autoplayOverlay.abTestConfig?.status ?? 'draft' as const,
    trafficDistribution: autoplayOverlay.abTestConfig?.trafficDistribution ?? {},
    variantTrafficDistribution: autoplayOverlay.abTestConfig?.variantTrafficDistribution ?? {},
    isRealTimeEnabled: autoplayOverlay.abTestConfig?.isRealTimeEnabled ?? false,
    startDate: autoplayOverlay.abTestConfig?.startDate,
    endDate: autoplayOverlay.abTestConfig?.endDate,
    lastUpdated: autoplayOverlay.abTestConfig?.lastUpdated ?? '',
    testId: autoplayOverlay.abTestConfig?.testId
  };

  const updateAutoplayOverlay = (key: keyof AutoplayOverlay, value: any) => {
    // Special handling for autoplay enabling
    if (key === 'enabled' && value) {
      // Ativar autoPlay automaticamente quando autoplayOverlay for ativado
      setFeature('autoPlay', true);

      if (features.showControls) {
        setFeature('showControls', false);
        toast.info(translateInterfaceText("Controles foram desativados automaticamente. Autoplay e controles não podem estar ativos ao mesmo tempo."));
      }
    } else if (key === 'enabled' && !value) {
      // Desativar autoPlay quando autoplayOverlay for desativado
      setFeature('autoPlay', false);
    }

    setFeature('autoplayOverlay', {
      ...autoplayOverlay,
      [key]: value
    });
  };

  const updateColors = (key: string, value: string) => {
    updateAutoplayOverlay('colors', {
      ...autoplayOverlay.colors,
      [key]: value
    });
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      updateAutoplayOverlay('templateId', templateId);
      updateAutoplayOverlay('useTemplate', true);
      updateAutoplayOverlay('colors', template.colors);
    }
  };

  const handleCustomTemplate = () => {
    updateAutoplayOverlay('useTemplate', false);
    updateAutoplayOverlay('templateId', 'custom');
    setActiveTab('customization');
  };

  // A/B Test functions
  const updateABTestConfig = (key: string, value: any) => {
    const updatedConfig = {
      ...abTestConfig,
      [key]: value
    };
    
    // Ativar tempo real quando o teste for iniciado
    if (key === 'enabled' && value === true) {
      updatedConfig.isRealTimeEnabled = true;
      updatedConfig.status = 'active';
    }
    
    updateAutoplayOverlay('abTestConfig', updatedConfig);
  };

  const handleTestTemplateSelection = (templateId: string, checked: boolean) => {
    const currentSelected = safeArray(abTestConfig.selectedTemplates);
    const newSelected = checked 
      ? [...currentSelected, templateId]
      : currentSelected.filter(id => id !== templateId);
    
    // Validate min/max templates
    if (checked && currentSelected.length >= 5) {
      toast.error(translateInterfaceText("Máximo de 5 modelos simultâneos permitidos"));
      return;
    }
    
    updateABTestConfig('selectedTemplates', newSelected);
    
    // Auto-generate uniform traffic distribution
    if (newSelected.length >= 2) {
      const uniformPercent = Math.floor(100 / newSelected.length);
      const remainder = 100 - (uniformPercent * newSelected.length);
      
      const newDistribution: Record<string, number> = {};
      newSelected.forEach((id, index) => {
        newDistribution[id] = index === 0 ? uniformPercent + remainder : uniformPercent;
      });
      
      updateABTestConfig('trafficDistribution', newDistribution);
    }
  };

  const handleCopyTestCode = () => {
    const testId = `test-${Date.now()}`;
    const embedCode = `<script>
// Código de embed do teste A/B automático
window.autoplayABTest = {
  testId: "${testId}",
  templates: ${JSON.stringify(abTestConfig.selectedTemplates)},
  trafficDistribution: ${JSON.stringify(abTestConfig.trafficDistribution)},
  enabled: true
};
</script>`;
    
    navigator.clipboard.writeText(embedCode);
    toast.success(translateInterfaceText("Código copiado para a área de transferência!"));
  };
  
  const handleApplyTest = () => {
    const selectedTemplates = safeArray(abTestConfig.selectedTemplates);
    if (selectedTemplates.length < 2) {
      toast.error(translateInterfaceText("Selecione pelo menos 2 modelos para iniciar o teste"));
      return;
    }
    
    const totalPercent = Object.values(abTestConfig.trafficDistribution || {})
      .reduce((sum, percent) => sum + percent, 0);
    
    if (Math.abs(totalPercent - 100) > 0.1) {
      toast.error(translateInterfaceText("A soma da distribuição de tráfego deve ser 100%"));
      return;
    }
    
    updateABTestConfig('lastUpdated', new Date().toISOString());
    toast.success(translateInterfaceText("Teste aplicado com sucesso!"));
  };
  
  const updateTrafficDistribution = (templateId: string, percent: number) => {
    const newDistribution = {
      ...abTestConfig.trafficDistribution,
      [templateId]: percent
    };
    updateABTestConfig('trafficDistribution', newDistribution);
  };

  const handleFinishTest = () => {
    updateABTestConfig('enabled', false);
    updateABTestConfig('status', 'finished');
    updateABTestConfig('isRealTimeEnabled', false);
    setShowFinalizeModal(false);
    toast.success(translateInterfaceText("Teste finalizado com sucesso!"));
  };

  const handleTrafficDistributionChange = (distribution: Record<string, number>) => {
    updateABTestConfig('variantTrafficDistribution', distribution);
    toast.success(translateInterfaceText("Distribuição de tráfego atualizada com sucesso!"));
  };

  const handleResetData = () => {
    // Mock reset functionality
    setLastUpdated(new Date());
    toast.success(translateInterfaceText("Dados resetados com sucesso!"));
  };

  // Calculate if traffic distribution is valid (sums to 100%)
  const isValidDistribution = () => {
    const selectedTemplates = safeArray(abTestConfig.selectedTemplates);
    if (selectedTemplates.length === 0) return true;
    
    const distribution = abTestConfig.trafficDistribution || {};
    const total = selectedTemplates.reduce((sum, templateId) => {
      return sum + (distribution[templateId] || 0);
    }, 0);
    
    return Math.abs(total - 100) < 0.01; // Allow for floating point precision
  };

  const getDistributionTotal = () => {
    const selectedTemplates = safeArray(abTestConfig.selectedTemplates);
    const distribution = abTestConfig.trafficDistribution || {};
    return selectedTemplates.reduce((sum, templateId) => {
      return sum + (distribution[templateId] || 0);
    }, 0);
  };

  const getDistributionMessage = () => {
    const total = getDistributionTotal();
    const isValid = isValidDistribution();
    
    if (isValid && total === 100) {
      return {
        type: 'success' as const,
        message: translateInterfaceText("Distribuição válida: 100%")
      };
    }
    
    return {
      type: 'error' as const,
      message: translateInterfaceText("Distribuição inválida: {value0}% (deve somar 100%)", { value0: (total.toFixed(1)) })
    };
  };

  // Hook para métricas em tempo real
  const { metrics: realTimeMetrics } = useAutoplayRealTimeMetrics({
    selectedTemplates: safeArray(abTestConfig.selectedTemplates),
    isEnabled: abTestConfig.isRealTimeEnabled || false,
    testStatus: abTestConfig.status
  });

  // Update last updated time simulation
  useEffect(() => {
    if (abTestConfig.enabled) {
      const interval = setInterval(() => {
        setLastUpdated(new Date());
      }, 300000); // Update every 5 minutes
      
      return () => clearInterval(interval);
    }
  }, [abTestConfig.enabled]);

  const isEnabled = autoplayOverlay.enabled;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Conteúdo principal com scroll */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        
        {/* TOGGLE PRINCIPAL - Estilo igual PlayerThemeCard */}
        <div 
          className={cn(
            "bg-white rounded-[14px] p-4 mb-6 shadow-sm",
            "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isEnabled 
              ? "border border-[#1D4FFF]/15 bg-[#F5F8FF]" 
              : "border border-[#EAEAEA]"
          )}
        >
          <div className="flex items-center gap-3">
            {/* Icon Box */}
            <div
              className={cn(
                "flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300",
                isEnabled 
                  ? "bg-[#1D4FFF]/10 text-[#1D4FFF]" 
                  : "bg-[#E8F0FF] text-[#6B7280]"
              )}
            >
              <PlayCircle className="w-5 h-5" strokeWidth={2} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h4 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">
                {translateInterfaceText("Ativar Autoplay")}</h4>
              <p className="text-[12px] text-[#6B7280] leading-snug mt-0.5">
                {translateInterfaceText("Garanta maior retenção iniciando o vídeo automaticamente.")}</p>
            </div>

            {/* Toggle */}
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => updateAutoplayOverlay('enabled', checked)}
              className="flex-shrink-0 data-[state=checked]:bg-[#1D4FFF]"
            />
          </div>
        </div>

        {/* CONTEÚDO QUANDO ATIVADO - FASE 01: Apenas Visual */}
        <div className={cn("flex flex-col flex-1 min-h-0", !isEnabled && "hidden")}>
          {/* TABS - Apenas Visual, Teste AB, Métricas */}
          <Tabs value={activeTab} onValueChange={(tab) => {
            setActiveTab(tab);
            onActiveTabChange?.(tab);
          }} className="w-full flex flex-col flex-1 min-h-0 overflow-hidden">
              <TabsList className="w-full bg-[#F7F9FC] rounded-xl p-1 mb-4">
                <TabsTrigger 
                  value="basic"
                  className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
                >
                  {translateInterfaceText("Personalização")}</TabsTrigger>
                <TabsTrigger 
                  value="automatic-test"
                  className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
                >
                  {translateInterfaceText("Teste AB")}</TabsTrigger>
                <TabsTrigger 
                  value="metrics"
                  className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
                >
                  {translateInterfaceText("Métricas")}</TabsTrigger>
              </TabsList>

              {/* Aba 1: Personalização Visual (100% visual) */}
              <TabsContent value="basic" className="h-full flex-1 overflow-hidden px-0 py-0">
                <AutoplayPersonalizationTab features={features} setFeature={setFeature} />
              </TabsContent>

              {/* Aba 2: Teste AB */}
              <TabsContent value="automatic-test" className="flex-1 min-h-0 flex flex-col px-0 py-0 mt-0 overflow-hidden h-full">
                <AutoplayTestTab features={features} setFeature={setFeature} />
              </TabsContent>

              {/* Aba 3: Métricas */}
              <TabsContent value="metrics" className="h-full flex-1 overflow-hidden px-0 py-0">
                <AutoplayMetricsTab features={features} setFeature={setFeature} />
              </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Modal de finalização */}
      <FinalizeAutoplayTestModal
        isOpen={showFinalizeModal}
        onOpenChange={setShowFinalizeModal}
        onConfirm={handleFinishTest}
        testId={abTestConfig.testId}
      />

      {/* Modal de distribuição de tráfego */}
      <TrafficEditModal
        open={trafficModalOpen}
        onOpenChange={setTrafficModalOpen}
        allVideos={mockAutoplayVideos}
        onSave={(distribution) => {
          console.log('Nova distribuição de tráfego:', distribution);
          setTrafficModalOpen(false);
        }}
      />

      {/* Modal de plataformas conectadas */}
      <ConnectedPlatformsModal 
        open={platformsModalOpen}
        onOpenChange={setPlatformsModalOpen}
        platforms={mockPlatforms}
        containerRef={containerRef}
      />
    </div>
  );
};